import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { channelPartnerAuthAPI } from '../../services/api';
import { Card, Spinner, PageHeader } from '../../components/UI';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Groups the same org status values used in PartnerOrganizations.jsx into a
// left-to-right referral funnel, so a partner can see progress at a glance.
const STAGES = [
  { key: 'trial', label: 'Trial', statuses: ['trial'], color: 'amber' },
  { key: 'active', label: 'Active / Paying', statuses: ['active'], color: 'green' },
  { key: 'at_risk', label: 'At Risk', statuses: ['expired', 'suspended'], color: 'red' },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'], color: 'gray' },
];

const COLOR_CLASSES = {
  amber: { dot: 'bg-amber-400', header: 'text-amber-700 bg-amber-50' },
  green: { dot: 'bg-green-400', header: 'text-green-700 bg-green-50' },
  red: { dot: 'bg-red-400', header: 'text-red-700 bg-red-50' },
  gray: { dot: 'bg-secondary-400', header: 'text-secondary-700 bg-secondary-100' },
};

const PartnerPipeline = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['partner-organizations-pipeline'],
    queryFn: () => channelPartnerAuthAPI.getOrganizations({ limit: 200 }),
  });

  const organizations = data?.data?.data || [];

  return (
    <div>
      <PageHeader
        title="Pipeline of Progress"
        description="Where each business you referred stands right now"
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : organizations.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <HiOutlineOfficeBuilding className="w-10 h-10 text-secondary-300 mx-auto mb-3" />
            <p className="font-semibold text-secondary-700">No referrals yet</p>
            <p className="text-sm text-secondary-400 mt-1">
              Businesses that register with your referral code will appear here, grouped by stage.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STAGES.map((stage) => {
            const orgsInStage = organizations.filter((o) => stage.statuses.includes(o.status));
            const colors = COLOR_CLASSES[stage.color];
            return (
              <div key={stage.key} className="flex flex-col">
                <div className={`flex items-center justify-between rounded-xl px-3 py-2 mb-3 ${colors.header}`}>
                  <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                    {stage.label}
                  </span>
                  <span className="text-xs font-bold">{orgsInStage.length}</span>
                </div>
                <div className="space-y-2 flex-1">
                  {orgsInStage.length === 0 ? (
                    <p className="text-xs text-secondary-300 px-1">None yet</p>
                  ) : (
                    orgsInStage.map((org) => (
                      <Card key={org._id} padding="p-3">
                        <p className="text-sm font-semibold text-secondary-900 truncate">{org.name}</p>
                        <p className="text-xs text-secondary-400 mt-0.5">{org.planId?.name || '—'}</p>
                        <p className="text-xs text-secondary-400 mt-1">Joined {formatDate(org.createdAt)}</p>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartnerPipeline;
